import { useState, useRef, useEffect, useCallback } from "react";
import {
  Send, Plus, Trash2, Bot, User, Copy, Check, Sparkles,
  MessageSquare, ChevronRight, Loader2, AlertCircle, Menu, X,
} from "lucide-react";
import { sendChatMessage } from "./api";

// ─── Local Storage helpers ────────────────────────────────────────────────────
const STORAGE_KEY = "sb_ai_sessions";

function loadSessions() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

function saveSessions(sessions) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
}

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Markdown parser ──────────────────────────────────────────────────────────
function escapeHtml(str) {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function parseMarkdown(text) {
  if (!text) return "";

  const codeBlocks = [];
  let html = text;

  // Extract fenced code blocks
  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, _lang, code) => {
    const idx = codeBlocks.length;
    codeBlocks.push(
      `<pre style="background:var(--color-surface-alt);border:1px solid var(--color-border);border-radius:8px;padding:12px;margin:8px 0;overflow-x:auto;font-size:13px;font-family:monospace;line-height:1.5"><code>${escapeHtml(code.trim())}</code></pre>`
    );
    return `\x00CB${idx}\x00`;
  });

  // Inline code
  html = html.replace(/`([^`\n]+)`/g, (_, c) =>
    `<code style="background:var(--color-surface-alt);border:1px solid var(--color-border);padding:1px 5px;border-radius:4px;font-size:13px;font-family:monospace">${escapeHtml(c)}</code>`
  );

  // Bold + Italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

  // Headers
  html = html.replace(/^#### (.+)$/gm, '<p style="font-size:13px;font-weight:600;margin:8px 0 2px">$1</p>');
  html = html.replace(/^### (.+)$/gm, '<p style="font-size:14px;font-weight:700;margin:10px 0 4px">$1</p>');
  html = html.replace(/^## (.+)$/gm, '<p style="font-size:15px;font-weight:700;margin:12px 0 4px">$1</p>');
  html = html.replace(/^# (.+)$/gm, '<p style="font-size:16px;font-weight:800;margin:12px 0 4px">$1</p>');

  // HR
  html = html.replace(/^---$/gm, '<hr style="border:none;border-top:1px solid var(--color-border);margin:12px 0" />');

  // Lists
  const lines = html.split("\n");
  const out = [];
  let inUl = false;
  let inOl = false;

  for (const line of lines) {
    const ulM = line.match(/^[\-\*\+] (.+)/);
    const olM = line.match(/^\d+\. (.+)/);
    if (ulM) {
      if (inOl) { out.push("</ol>"); inOl = false; }
      if (!inUl) { out.push('<ul style="padding-left:20px;margin:6px 0">'); inUl = true; }
      out.push(`<li style="margin:3px 0">${ulM[1]}</li>`);
    } else if (olM) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      if (!inOl) { out.push('<ol style="padding-left:20px;margin:6px 0">'); inOl = true; }
      out.push(`<li style="margin:3px 0">${olM[1]}</li>`);
    } else {
      if (inUl) { out.push("</ul>"); inUl = false; }
      if (inOl) { out.push("</ol>"); inOl = false; }
      out.push(line);
    }
  }
  if (inUl) out.push("</ul>");
  if (inOl) out.push("</ol>");
  html = out.join("\n");

  // Paragraphs (double newline)
  html = html
    .split(/\n\n+/)
    .map((para) => {
      para = para.trim();
      if (!para) return "";
      if (
        para.startsWith("<p ") || para.startsWith("<ul") || para.startsWith("<ol") ||
        para.startsWith("<pre") || para.startsWith("<hr") || para.startsWith("\x00CB")
      ) {
        return para.replace(/\n/g, "");
      }
      return `<p style="margin:0 0 8px;line-height:1.65">${para.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("");

  // Restore code blocks
  codeBlocks.forEach((block, idx) => {
    html = html.split(`\x00CB${idx}\x00`).join(block);
  });

  return html;
}

// ─── Quick prompts ────────────────────────────────────────────────────────────
const QUICK_PROMPTS = [
  "Show me overdue projects",
  "Who is currently online?",
  "Summarize today's dashboard",
  "List all IN_PROGRESS projects",
  "How many projects are done this month?",
  "Show admin performance stats",
  "Any unread notifications?",
  "List all employees and their workload",
];

// ─── Components ───────────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-end gap-3 max-w-[80%]">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-sm">
        <Bot size={14} className="text-white" />
      </div>
      <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-surface border border-border shadow-sm">
        <div className="flex gap-1.5 items-center h-4">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-text_muted"
              style={{
                animation: "aiDot 1.2s ease-in-out infinite",
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={copy}
      title="Copy"
      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-surface_alt text-text_muted hover:text-text_secondary"
    >
      {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
    </button>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex items-end gap-3 group ${isUser ? "flex-row-reverse" : ""}`}>
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
          isUser ? "bg-secondary" : "bg-primary"
        }`}
      >
        {isUser ? <User size={14} className="text-white" /> : <Bot size={14} className="text-white" />}
      </div>

      {/* Bubble */}
      <div className={`relative max-w-[78%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-4 py-3 rounded-2xl shadow-sm text-sm ${
            isUser
              ? "bg-primary text-white rounded-br-sm"
              : "bg-surface border border-border text-text_primary rounded-bl-sm"
          }`}
        >
          {isUser ? (
            <p className="leading-relaxed whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <div
              className="ai-md leading-relaxed"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }}
            />
          )}
        </div>

        {/* Actions row */}
        <div className={`flex items-center gap-1 mt-1 px-1 ${isUser ? "flex-row-reverse" : ""}`}>
          <span className="text-[10px] text-text_muted">
            {new Date(msg.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
          {!isUser && <CopyButton text={msg.content} />}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AiAssistantPage() {
  const [sessions, setSessions] = useState(loadSessions);
  const [activeId, setActiveId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const activeSession = sessions.find((s) => s.id === activeId) || null;
  const messages = activeSession?.messages || [];

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Persist sessions
  useEffect(() => {
    saveSessions(sessions);
  }, [sessions]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
  }, [input]);

  const newChat = useCallback(() => {
    const id = generateId();
    const session = { id, title: "New conversation", messages: [], createdAt: Date.now() };
    setSessions((prev) => [session, ...prev]);
    setActiveId(id);
    setInput("");
    setError(null);
  }, []);

  const deleteSession = useCallback((id, e) => {
    e.stopPropagation();
    setSessions((prev) => prev.filter((s) => s.id !== id));
    if (activeId === id) setActiveId(null);
  }, [activeId]);

  const sendMessage = useCallback(async () => {
    const content = input.trim();
    if (!content || loading) return;

    let sessionId = activeId;

    // Create new session if none active
    if (!sessionId) {
      sessionId = generateId();
      const newSession = {
        id: sessionId,
        title: content.slice(0, 50),
        messages: [],
        createdAt: Date.now(),
      };
      setSessions((prev) => [newSession, ...prev]);
      setActiveId(sessionId);
    }

    const userMsg = { role: "user", content, ts: Date.now() };

    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        const updated = [...s.messages, userMsg];
        return {
          ...s,
          messages: updated,
          title: s.messages.length === 0 ? content.slice(0, 50) : s.title,
        };
      })
    );

    setInput("");
    setLoading(true);
    setError(null);

    try {
      // Build history for API (exclude timestamps, only role+content)
      const currentSession = sessions.find((s) => s.id === sessionId);
      const history = [
        ...(currentSession?.messages || []).map(({ role, content: c }) => ({ role, content: c })),
        { role: "user", content },
      ];

      const res = await sendChatMessage(history);

      if (!res.status) throw new Error(res.message || "Request failed");

      const aiMsg = {
        role: "assistant",
        content: res.data.message,
        ts: Date.now(),
      };

      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId ? { ...s, messages: [...s.messages, aiMsg] } : s
        )
      );
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [input, loading, activeId, sessions]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInput(prompt);
    textareaRef.current?.focus();
  };

  const isEmpty = messages.length === 0;

  return (
    <>
      {/* Keyframe injection */}
      <style>{`
        @keyframes aiDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .ai-md p:last-child { margin-bottom: 0; }
        .ai-md ul, .ai-md ol { list-style: revert; }
      `}</style>

      <div className="flex h-full overflow-hidden bg-background">

        {/* ── Left Sidebar ── */}
        <div
          className={`flex-shrink-0 flex flex-col border-r border-border bg-surface transition-all duration-200 overflow-hidden ${
            sidebarOpen ? "w-64" : "w-0"
          }`}
        >
          <div className="flex flex-col h-full min-w-[256px]">
            {/* Sidebar header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Sparkles size={15} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text_primary leading-tight">SwiftBoard AI</p>
                  <p className="text-[10px] text-text_muted">Powered by GPT-4o</p>
                </div>
              </div>
              <button
                onClick={newChat}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary_hover transition-colors"
              >
                <Plus size={15} />
                New chat
              </button>
            </div>

            {/* Sessions list */}
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {sessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                  <MessageSquare size={28} className="text-text_muted mb-2" />
                  <p className="text-xs text-text_muted">No conversations yet</p>
                </div>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setActiveId(s.id); setError(null); }}
                    className={`w-full group flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      s.id === activeId
                        ? "bg-primary_tint text-primary"
                        : "hover:bg-surface_alt text-text_secondary"
                    }`}
                  >
                    <MessageSquare size={13} className="flex-shrink-0" />
                    <span className="flex-1 text-xs font-medium truncate">{s.title}</span>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-danger transition-all"
                    >
                      <Trash2 size={11} />
                    </button>
                  </button>
                ))
              )}
            </div>

            {/* Sidebar footer */}
            <div className="p-3 border-t border-border">
              <p className="text-[10px] text-text_muted text-center">
                Conversations stored locally
              </p>
            </div>
          </div>
        </div>

        {/* ── Main Chat Area ── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Header */}
          <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-border bg-surface">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="p-1.5 rounded-lg hover:bg-surface_alt text-text_muted hover:text-text_primary transition-colors"
            >
              {sidebarOpen ? <X size={17} /> : <Menu size={17} />}
            </button>
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Bot size={14} className="text-white" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-text_primary leading-tight">AI Assistant</h1>
              <p className="text-[10px] text-text_muted">Ask anything about SwiftBoard data</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-5">
            {isEmpty ? (
              /* Welcome state */
              <div className="flex flex-col items-center justify-center h-full max-w-lg mx-auto text-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center shadow-lg">
                  <Sparkles size={28} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text_primary mb-2">SwiftBoard AI</h2>
                  <p className="text-sm text-text_secondary leading-relaxed">
                    Ask me about projects, users, analytics, notifications, or anything in your SwiftBoard portal.
                  </p>
                </div>
                <div className="w-full grid grid-cols-2 gap-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => handleQuickPrompt(p)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-surface border border-border text-left text-xs text-text_secondary hover:border-primary hover:text-primary hover:bg-primary_tint transition-all group"
                    >
                      <ChevronRight size={12} className="flex-shrink-0 text-text_muted group-hover:text-primary" />
                      <span>{p}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => <Message key={i} msg={msg} />)
            )}

            {loading && <TypingIndicator />}

            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-danger_bg border border-danger_border text-danger text-sm">
                <AlertCircle size={15} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick prompts bar (when chat is active) */}
          {!isEmpty && (
            <div className="flex-shrink-0 px-4 py-2 border-t border-border overflow-x-auto">
              <div className="flex gap-2 w-max">
                {QUICK_PROMPTS.slice(0, 4).map((p) => (
                  <button
                    key={p}
                    onClick={() => handleQuickPrompt(p)}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full bg-surface border border-border text-xs text-text_secondary hover:border-primary hover:text-primary hover:bg-primary_tint transition-all"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="flex-shrink-0 p-4 border-t border-border bg-surface">
            <div className="flex items-end gap-3 max-w-4xl mx-auto">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about SwiftBoard… (Enter to send, Shift+Enter for new line)"
                  rows={1}
                  disabled={loading}
                  className="w-full resize-none rounded-xl border border-border bg-background text-text_primary placeholder-text_muted text-sm px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all disabled:opacity-50"
                  style={{ minHeight: "48px", maxHeight: "140px" }}
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary text-white flex items-center justify-center hover:bg-primary_hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              >
                {loading ? (
                  <Loader2 size={17} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
            <p className="text-center text-[10px] text-text_muted mt-2">
              AI can make mistakes. Verify important information.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
